// src/hooks/useOrdersPermissions.ts
import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { ORDERS_PERMISSIONS } from '../constants/permissions.constants';

/**
 * Hook to get all orders/sales-related permissions in one object
 *
 * Used for order operations: view, add, pay, return, exchange
 *
 * @returns Object with all order permission flags
 *
 * @example
 * ```tsx
 * const ordersPerms = useOrdersPermissions();
 * if (ordersPerms.canPay) {
 *   // Show payment button
 * }
 * ```
 */
export const useOrdersPermissions = () => {
  const canView = usePermission(ORDERS_PERMISSIONS.VIEW);
  const canViewRow = usePermission(ORDERS_PERMISSIONS.VIEW_ROW);
  const canPay = usePermission(ORDERS_PERMISSIONS.PAY);
  const canReturn = usePermission(ORDERS_PERMISSIONS.RETURN);
  const canExchange = usePermission(ORDERS_PERMISSIONS.EXCHANGE);
  const canEdit = usePermission(ORDERS_PERMISSIONS.EDIT);
  const canAdd = usePermission(ORDERS_PERMISSIONS.ADD);
  const canModifySaleDate = usePermission(ORDERS_PERMISSIONS.MODIFY_SALE_DATE);
  /** Corregir fecha comercial de una venta ya registrada */
  const canEditSaleDate = usePermission(ORDERS_PERMISSIONS.EDIT_SALE_DATE);
  /**
   * Venta multi-sucursal (permiso de rol único): catálogo remoto + confirmar venta mixta.
   * El envío se completa automáticamente al recibir el último traslado.
   */
  const canMixedSell = usePermission(ORDERS_PERMISSIONS.MIXED_SELL);
  /** Permite elegir otro usuario de la misma sucursal como vendedor al registrar una venta */
  const canChangeSeller = usePermission(ORDERS_PERMISSIONS.CHANGE_SELLER);

  return useMemo(
    () => ({
      canView,
      canViewRow,
      canPay,
      canReturn,
      canExchange,
      canEdit,
      canAdd,
      canModifySaleDate,
      canEditSaleDate,
      canMixedSell,
      canChangeSeller,
    }),
    [
      canView,
      canViewRow,
      canPay,
      canReturn,
      canExchange,
      canEdit,
      canAdd,
      canModifySaleDate,
      canEditSaleDate,
      canMixedSell,
      canChangeSeller,
    ],
  );
};
